/**
 * Incidents IT › Corrections (décision du chef de projet du 28/10/2026, D-IT-036, US #141) : règles pures partagées par
 * l'API, le front et le mode maquette.
 *
 * Le titre, la description ou un commentaire d'un incident se corrigent jusqu'au statut « résolu », non compris. Chaque
 * correction AJOUTE au fil un commentaire « trace » (jamais un commentaire modifié ni supprimé) :
 *  - titre, description : la trace garde le texte d'origine (le nouveau est sur l'incident) ;
 *  - commentaire : la trace porte le nouveau texte et renvoie au message corrigé, resté intact, dont elle prend la
 *    visibilité (la correction d'une note interne reste une note interne, jamais lue par le demandeur). Le fil affiche
 *    la dernière version ; les précédentes se consultent dans l'historique du message (D-IT-129).
 * Le texte n'est jamais copié dans l'audit ni dans une notification.
 */
import { auteurCommentaire, refusCommentaire } from './commentaires';
import type { RefusActeurIncident, StatutIncident } from './incidents';

/** Ce qu'on corrige ; aussi la nature d'une trace (colonne it.commentaire.correction). */
export const CIBLES_CORRECTION = ['titre', 'description', 'commentaire'] as const;
export type CibleCorrection = (typeof CIBLES_CORRECTION)[number];

/** Statuts où l'incident se corrige : jusqu'à « résolu », non compris (un incident rouvert redevient corrigeable). */
export const STATUTS_CORRECTION: readonly StatutIncident[] = ['nouveau', 'qualifie', 'affecte', 'en-traitement', 'en-attente'];

/** L'incident se corrige encore : statut avant « résolu » et non archivé ; sinon 409 etat_incompatible. */
export function correctionPossible(incident: { statut: StatutIncident; archiveLe: string | null }): boolean {
  return !incident.archiveLe && STATUTS_CORRECTION.includes(incident.statut);
}

/** Refus d'une correction (403) : ceux des commentaires, plus `non-auteur` (message d'un autre). */
export type RefusCorrection = RefusActeurIncident | 'non-auteur';

/**
 * Refus d'acteur d'une correction (403), ou `null` :
 *  - titre, description : le demandeur (même agent : il corrige alors en tant que demandeur, D-IT-124 ne vise que le
 *    traitement) et tout agent qui voit l'incident, pas seulement l'agent affecté (D-IT-126, décidé) ; `non-demandeur`
 *    sinon (déclarant non agent compris) ;
 *  - commentaire : son auteur seul, s'il peut encore écrire cette visibilité (refusCommentaire : un ancien agent ne
 *    corrige plus sa note interne). PROVISOIRE (D-IT-126, #408) : ni un agent ni le demandeur ne corrigent le message
 *    d'un autre.
 */
export function refusCorrection(
  cible: CibleCorrection,
  incident: { demandeur: string; declarant: string },
  moi: string,
  estAgent: boolean,
  message?: { auteur: string; interne: boolean },
): RefusCorrection | null {
  if (cible !== 'commentaire') {
    return incident.demandeur === moi || auteurCommentaire(incident, moi, estAgent) === 'agent' ? null : 'non-demandeur';
  }
  if (!message || message.auteur !== moi) return 'non-auteur';
  return refusCommentaire(message, incident, moi, estAgent);
}

/** Message d'origine (pas une trace) : seul un tel message se corrige ; ses corrections lui renvoient. */
export const estMessageOrigine = (c: { correction: CibleCorrection | null }) => c.correction === null;

/**
 * Auteur d'origine à prévenir d'une correction (#267, réponse de Benoit du 02/11/2026), ou `null` : il est prévenu dès
 * qu'un autre corrige son contenu, sans détection de faute de frappe (trop fragile). L'auteur du titre et de la
 * description est le demandeur (D-IT-036) ; celui d'un message, son auteur. Qui corrige son propre contenu ne prévient
 * personne, pas même l'agent affecté. La notification ne porte jamais le texte.
 */
export function auteurAPrevenir(
  cible: CibleCorrection,
  incident: { demandeur: string },
  moi: string,
  message?: { auteur: string },
): string | null {
  const auteur = cible === 'commentaire' ? message?.auteur : incident.demandeur;
  return auteur && auteur.toLowerCase() !== moi.toLowerCase() ? auteur : null;
}
